// src/components/initial-inventory/SerializedItemEntry.tsx
// Serial number batch entry form for Initial Inventory workflow
// Supports one-per-line and CSV paste formats
// Updated: Added body armor/expiration tracking fields for categories that require it

import React, { useState, useEffect } from 'react';
import { Hash, MapPin, AlertTriangle, Shield } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { CategoryHierarchy } from '../../types/category';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { requiresExpiration, getDefaultExpirationMonths, calculateExpirationDate } from '../../utils/categoryTracking';

interface BodyArmorData {
  ballisticLevel: string;
  size: string;
  expirationDate: string;
}

interface SerializedItemEntryProps {
  product: Product;
  categories: CategoryHierarchy[];
  onSubmit: (
    serialNumbers: string[],
    location: string,
    condition: string,
    notes?: string,
    bodyArmor?: BodyArmorData
  ) => Promise<void>;
  submitting: boolean;
}

const CONDITION_OPTIONS = [
  { value: 'Excellent', label: 'Excellent', description: 'Like new condition' },
  { value: 'Good', label: 'Good', description: 'Normal wear, fully functional' },
  { value: 'Fair', label: 'Fair', description: 'Visible wear, functional' },
  { value: 'Poor', label: 'Poor', description: 'Heavy wear, may need attention' },
  { value: 'Needs Repair', label: 'Needs Repair', description: 'Requires maintenance' }
];

const BALLISTIC_LEVELS = ['IIA', 'II', 'IIIA', 'III', 'IV'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

const SerializedItemEntry: React.FC<SerializedItemEntryProps> = ({
  product,
  categories,
  onSubmit,
  submitting
}) => {
  // Form state
  const [serialInput, setSerialInput] = useState('');
  const [location, setLocation] = useState(product.defaultLocation || '');
  const [condition, setCondition] = useState('Good');
  const [notes, setNotes] = useState('');
  
  // Body armor state
  const [ballisticLevel, setBallisticLevel] = useState('IIIA');
  const [size, setSize] = useState('L');
  const [expirationDate, setExpirationDate] = useState('');
  
  // Parsed serial numbers
  const [parsedSerials, setParsedSerials] = useState<string[]>([]);
  const [duplicates, setDuplicates] = useState<string[]>([]);
  
  // Storage locations
  const [storageLocations, setStorageLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  
  // Category expiration requirement
  const [needsExpiration, setNeedsExpiration] = useState(false);

  // Check if category requires expiration tracking
  useEffect(() => {
    const subcategoryId = product.subcategoryId || product.categoryId;
    const subcategory = categories.find(c => c.categoryId === subcategoryId);
    
    if (subcategory) {
      const needsExp = requiresExpiration(subcategory);
      setNeedsExpiration(needsExp);
      
      // Set default expiration date if configured
      if (needsExp) {
        const defaultMonths = getDefaultExpirationMonths(subcategory);
        if (defaultMonths) {
          const expDate = calculateExpirationDate(new Date(), defaultMonths);
          setExpirationDate(expDate);
        }
      }
    }
  }, [product, categories]);

  // Load storage locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await tenantConfigAPI.getInventoryLocations();
        setStorageLocations(locations);
        
        // Set default if product has one and it's in the list
        if (product.defaultLocation && locations.includes(product.defaultLocation)) {
          setLocation(product.defaultLocation);
        } else if (locations.length > 0 && !location) {
          setLocation(locations[0]);
        }
      } catch (err) {
        console.error('Failed to load storage locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, [product.defaultLocation]);

  // Parse serial numbers from input
  useEffect(() => {
    if (!serialInput.trim()) {
      setParsedSerials([]);
      setDuplicates([]);
      return;
    }

    // Split by newlines, commas, tabs, or semicolons
    const raw = serialInput
      .split(/[\n,\t;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);

    // Find duplicates
    const seen = new Set<string>();
    const dupes: string[] = [];
    const unique: string[] = [];

    raw.forEach(serial => {
      const normalized = serial.toUpperCase();
      if (seen.has(normalized)) {
        if (!dupes.includes(serial)) {
          dupes.push(serial);
        }
      } else {
        seen.add(normalized);
        unique.push(serial);
      }
    });

    setParsedSerials(unique);
    setDuplicates(dupes);
  }, [serialInput]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parsedSerials.length === 0) {
      return;
    }

    if (!location) {
      return;
    }

    // Validate body armor fields if required
    if (needsExpiration) {
      if (!ballisticLevel || !size || !expirationDate) {
        return;
      }
    }

    // Build body armor data if needed
    const bodyArmorData = needsExpiration ? {
      ballisticLevel,
      size,
      expirationDate
    } : undefined;

    await onSubmit(parsedSerials, location, condition, notes || undefined, bodyArmorData);
    
    // Clear only the serial numbers on success, keep location/condition/body armor
    setSerialInput('');
    setNotes('');
  };

  const getCategoryPath = (categoryId: string): string => {
    const category = categories.find(c => c.categoryId === categoryId);
    if (!category) return '';
    
    if (category.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === category.parentCategoryId);
      return parent ? `${parent.name} › ${category.name}` : category.name;
    }
    
    return category.name;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Product Info Banner */}
      <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
        <div className="text-sm">
          <span className="text-blue-700 font-medium">Serialized Item</span>
          <span className="text-blue-600 mx-2">•</span>
          <span className="text-blue-600">{getCategoryPath(product.categoryId)}</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
            Each item tracked individually
          </span>
          {needsExpiration && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded flex items-center">
              <Shield className="w-3 h-3 mr-1" />
              Expiration Tracked
            </span>
          )}
        </div>
      </div>

      {/* Serial Numbers Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Hash className="w-4 h-4 inline mr-1" />
          Serial Numbers <span className="text-red-500">*</span>
        </label>
        <textarea
          value={serialInput}
          onChange={(e) => setSerialInput(e.target.value)}
          placeholder="Enter serial numbers (one per line, or comma/tab separated)&#10;&#10;Example:&#10;ABC123&#10;ABC124&#10;ABC125"
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          disabled={submitting}
        />
        
        {/* Serial Count & Duplicates Warning */}
        <div className="mt-2 flex items-center justify-between text-sm">
          <span className={`${parsedSerials.length > 0 ? 'text-green-600' : 'text-gray-500'}`}>
            {parsedSerials.length} serial number{parsedSerials.length !== 1 ? 's' : ''} detected
          </span>
          
          {duplicates.length > 0 && (
            <span className="text-amber-600 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {duplicates.length} duplicate{duplicates.length !== 1 ? 's' : ''} removed
            </span>
          )}
        </div>
      </div>

      {/* Location & Condition Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            Storage Location <span className="text-red-500">*</span>
          </label>
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={locationsLoading || submitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            required
          >
            <option value="">Select location...</option>
            {storageLocations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          {!locationsLoading && storageLocations.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              No storage locations configured. Add them in Admin → Tenant Settings.
            </p>
          )}
        </div>

        {/* Condition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Condition <span className="text-red-500">*</span>
          </label>
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            disabled={submitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            {CONDITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {CONDITION_OPTIONS.find(o => o.value === condition)?.description}
          </p>
        </div>
      </div>

      {/* Body Armor Fields (conditional) */}
      {needsExpiration && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-amber-800 mb-3 flex items-center">
            <Shield className="w-4 h-4 mr-2" />
            Body Armor Details
            <span className="ml-2 text-xs font-normal text-amber-600">
              (Applied to all items in this batch)
            </span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ballistic Level <span className="text-red-500">*</span>
              </label>
              <select
                value={ballisticLevel}
                onChange={(e) => setBallisticLevel(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {BALLISTIC_LEVELS.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size <span className="text-red-500">*</span>
              </label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
          <p className="text-xs text-amber-600 mt-3">
            Note: All items in this batch will share the same body armor specifications. 
            To add items with different specs, submit separate batches.
          </p>
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Existing department inventory at go-live"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={submitting}
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting || parsedSerials.length === 0 || !location || (needsExpiration && (!ballisticLevel || !size || !expirationDate))}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Adding Items...
            </>
          ) : (
            <>
              Add {parsedSerials.length} Item{parsedSerials.length !== 1 ? 's' : ''} to Inventory
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default SerializedItemEntry;
